import { useState, useCallback, useRef, useEffect } from "react";
import { Box, Alert, AlertIcon } from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import SuccessScreen from "components/Success/SuccessScreen";
import FormComponent from "./FormComponent";
import { useSessionJWT, useSessionCtx, useAnalytics } from "utils";
import { parseFinishCreateErrorForFieldLevelCodes, addressOnlyrequiredFields } from "./utils";
import { updateTiliaBankAccountUserAddress } from "api/payments/index.js";

const MissingAddressForm = ({ onSuccess, countryISO }) => {
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const { formatMessage } = useIntl();
  const { bankOwnerId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [updateAddressError, setUpdateAddressError] = useState(null);
  const [apiFieldLevelErrorCodes, setApiFieldLevelErrorCodes] = useState(null);
  const [renderSuccess, setRenderSuccess] = useState(false);

  const { sessionJWT } = useSessionJWT();
  const { sessionId } = useSessionCtx();

  useEffect(() => {
    if (!viewEventFired.current) {
      analytics.page("Banking", "MissingAddressForm_Viewed", { countryISO, bankOwnerId });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, countryISO, bankOwnerId]);

  const handlePostSuccessRender = () => {
    onSuccess && onSuccess();
  };

  const handleFormSubmit = useCallback(async (values) => {
    setIsLoading(true);
    setUpdateAddressError(null);
    setApiFieldLevelErrorCodes(null);
    try {
      if (!bankOwnerId) throw new Error("Missing bank owner ID.");
      const ctx = {
        ...sessionJWT,
        sessionId,
      };
      await updateTiliaBankAccountUserAddress(ctx, bankOwnerId, values);
      setRenderSuccess(true); // there's nothing in the response payload when successful, so if we're here, go to success page
    } catch (err) {
      const fieldLevelErrCodes = parseFinishCreateErrorForFieldLevelCodes(err, formatMessage);
      if (fieldLevelErrCodes) {
        setApiFieldLevelErrorCodes(fieldLevelErrCodes);
      }
      setUpdateAddressError(err);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setUpdateAddressError, setApiFieldLevelErrorCodes, bankOwnerId, sessionJWT, sessionId, formatMessage]);

  let content = null;

  if (renderSuccess) {
    content = <SuccessScreen onUnmount={handlePostSuccessRender} />;
  } else {
    content = (
      <FormComponent
        onSubmit={handleFormSubmit}
        requiredFields={addressOnlyrequiredFields}
        countryCode={countryISO}
        isLoading={isLoading}
        apiFieldLevelErrorCodes={apiFieldLevelErrorCodes}
        useAddressOnlyDescription={true}
      />
    );
  }

  let errMsg = null;
  if (updateAddressError) {
    const errCodes = updateAddressError.data?.codes;
    const errCodesMsg = (Array.isArray(errCodes) && errCodes.length > 0) ? `Error: ${errCodes.join(", ")}` : null;
    errMsg = errCodesMsg || updateAddressError.message || (
      <FormattedMessage
        defaultMessage="Apologies, there was an error. Please try again later."
        description="Error message"
      />
    );
  }

  return (
    <>
      <Box px="7" py="6">
        {content}
      </Box>
      {errMsg && (
        <Box px="7" pb="4">
          <Alert status="error">
            <AlertIcon />
            {errMsg}
          </Alert>
        </Box>
      )}
    </>
  );
};

export default MissingAddressForm;
